import { Bell01, SearchLg } from '@untitledui/icons'
import { Link } from 'react-router'
import { Avatar } from '@/components/base/avatar/avatar'
import { useShell } from '../store'
import { AccountMenu } from './account-menu'
import { useNotificationsDrawer } from './notifications-drawer'
import { PeriodSelector } from './period-selector'
import { initials } from './tree'

export interface Crumb { label: string, to?: string }

/**
 * PRD-018 §3.3: crumb, period, search, bell, person, in that order, 56 px, never scrolls away.
 *
 * The crumb starts at the page or the client, never at the firm (QA-18, Sean 2026-09-13 "drop firm root please"):
 * one firm per instance, and the tree header already names it top-left, so "Palomino Consulting /" in front of
 * every crumb repeated what the eye had just read. Firm pages read "Home", "Team"; object pages read
 * "Demo Retail Group / Demo Cultivation / Close". Karbon, Canopy and Linear do the same.
 */
export function PageBar({ crumbs, followsPeriod }: { crumbs: Crumb[], followsPeriod: boolean }) {
  const { bootstrap, setPaletteOpen } = useShell()
  const notifications = useNotificationsDrawer()
  return (
    <header role="banner" className="flex h-[var(--shell-pagebar-height)] shrink-0 items-center gap-4 border-b border-secondary bg-primary px-6">
      <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
        <ol className="flex items-center gap-1.5 text-sm" data-testid="breadcrumb">
          {crumbs.map((c, i) => (
            <li key={`${c.to ?? ''}#${c.label}`} className="flex items-center gap-1.5 min-w-0">
              {i > 0 ? <span aria-hidden className="text-quaternary">/</span> : null}
              {c.to && i < crumbs.length - 1 ? <Link to={c.to} className="truncate text-tertiary hover:text-primary">{c.label}</Link> : <span aria-current="page" className="truncate font-medium text-primary">{c.label}</span>}
            </li>
          ))}
        </ol>
      </nav>
      <PeriodSelector follows={followsPeriod} />
      <button type="button" onClick={() => setPaletteOpen(true)} className="flex h-9 w-56 items-center gap-2 rounded-lg border border-secondary bg-primary px-3 text-sm text-quaternary hover:bg-primary_hover" data-testid="search-field">
        <SearchLg className="size-4" />
        <span>Search</span>
        <kbd className="ml-auto rounded border border-secondary px-1 text-xs">⌘K</kbd>
      </button>
      {/* PRD-018 §3.3 (UX audit 2026-09-19 P1-7): the bell opens the notifications drawer over the current page; /my-work/notifications stays the full page. */}
      <button type="button" aria-label={`Notifications, ${bootstrap.counts.notificationsUnread} unread`} aria-haspopup="dialog" aria-expanded={notifications.open} onClick={notifications.show} className="relative rounded-md p-2 text-quaternary hover:bg-primary_hover" data-testid="notifications-bell">
        <Bell01 className="size-5" />
        {bootstrap.counts.notificationsUnread > 0 ? <span className="absolute right-1 top-1 size-2 rounded-full bg-brand-solid" /> : null}
      </button>
      <AccountMenu>
        <Avatar size="sm" initials={initials(bootstrap.me.name)} alt={bootstrap.me.name} />
      </AccountMenu>
    </header>
  )
}
